import {
    AccountEntity,
    AlbumEntity,
    SettingsEntity,
    TokenEntity,
    updateSettingsParams,
    UserMatchSettingsEntity,
    ValidationError,
} from '@interpals/api-web';
import { createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';

import ROUTES from 'routes';
import { createCustomToast } from 'services/toasts';
import { changeLocale } from 'utils/i18n';
import { initLoggedInUser } from 'redux/auth/actions';
import { ReduxState } from 'redux/root-reducer';
import { clearState } from '../auth/actions';
import api, { showApiErrorToast } from 'api';

interface UpdateAccountParams {
    id: string;
    birthday?: string;
    lang?: 'en-US' | 'ru-RU';
    home_city_id?: string;
    city_id?: string;
    name?: string;
    username?: string;
    password?: string;
    new_password?: string;
    email?: string;
    sex?: 'male' | 'female';
    tz?: string;
    website?: string;
    lfor?: string;
    push_likes?: boolean;
    push_profile_views?: boolean;
    push_comments?: boolean;
    push_messages?: boolean;
    push_birthdays?: boolean;
    push_feed_posts?: boolean;
    push_friend_requests?: boolean;
    email_likes?: boolean;
    email_profile_views?: boolean;
    email_comments?: boolean;
    email_messages?: boolean;
    email_birthdays?: boolean;
    email_feed_posts?: boolean;
    email_friend_requests?: boolean;
    email_reminders?: boolean;
    onsite_alerts?: boolean;
    searchable?: boolean;
    occupation?: string;
    education_desc?: string;
    relationship?: 'rel_not_specified' | 'rel_single' | 'rel_relationship' | 'rel_married' | 'rel_complicated';
    education_level?: 'edu_not_specified' | 'edu_high_school' | 'edu_college' | 'edu_bachelors' | 'edu_graduate';
    know?: string;
    learn?: string;
}

interface UpdateTextParams {
    id: string;
    description?: string;
    langex_desc?: string;
    tv?: string;
    quotes?: string;
    movies?: string;
    music?: string;
    hobbies?: string;
    books?: string;
    requests?: string;
}

const updateTextParamsOrder: Array<keyof UpdateTextParams> = [
    'description',
    'langex_desc',
    'tv',
    'quotes',
    'movies',
    'music',
    'hobbies',
    'books',
    'requests',
];

const updateAccountParamsOrder: Array<keyof UpdateAccountParams> = [
    'birthday',
    'lang',
    'home_city_id',
    'city_id',
    'name',
    'username',
    'password',
    'new_password',
    'email',
    'sex',
    'tz',
    'website',
    'lfor',
    'push_likes',
    'push_profile_views',
    'push_comments',
    'push_messages',
    'push_birthdays',
    'push_feed_posts',
    'push_friend_requests',
    'email_likes',
    'email_profile_views',
    'email_comments',
    'email_messages',
    'email_birthdays',
    'email_feed_posts',
    'email_friend_requests',
    'email_reminders',
    'onsite_alerts',
    'searchable',
    'occupation',
    'education_desc',
    'relationship',
    'education_level',
    'know',
    'learn',
];

export const updateAccount = createAsyncThunk<
    AccountEntity,
    { params: UpdateAccountParams; afterAction?: () => void },
    {
        rejectValue: ValidationError;
    }
>('account/update', async ({ params, afterAction }, { rejectWithValue }) => {
    return api
        .updateAccount({ ...params })
        .then((response) => {
            if (params.lang) {
                changeLocale(params.lang);
            }

            const shouldShowToast = updateAccountParamsOrder.some((key) => {
                const param = params[key];

                return (param || param === '') && typeof param !== 'boolean';
            });

            if (shouldShowToast && window.location.pathname !== ROUTES.ONBOARDING) {
                createCustomToast('success', `${i18next.t('Toast.SettingsSavedSuccessfully')}`);
            }

            if (afterAction) {
                afterAction();
            }

            return response;
        })
        .catch(async (err: unknown) => {
            if (err instanceof Response) {
                const errorBody = await err.json();

                const shouldShowErrorToast = updateAccountParamsOrder.some(
                    (key) => key === 'name' || key === 'username' || key === 'email',
                );

                if (err.status === 400) {
                    !shouldShowErrorToast && showApiErrorToast(err, i18next.t);

                    return rejectWithValue(errorBody as ValidationError);
                }
            }

            throw err;
        });
});

export const verifyAccount = createAsyncThunk('account/verification/update', async (_, { rejectWithValue }) => {
    return api
        .getAccount({ id: 'self' })
        .then((response) => {
            !response.email.verified && createCustomToast('warning', `${i18next.t('Toast.AccountVerificationFailed')}`);

            return response;
        })
        .catch((err) => rejectWithValue(err));
});

export const getAccount = createAsyncThunk('get/account', async () => {
    return api.getAccount({ id: 'self' });
});

export const updateAccountSettings = createAsyncThunk<SettingsEntity, Partial<updateSettingsParams>>(
    'account/settings/update',
    async (params, { rejectWithValue }) => {
        return api
            .updateSettings({ ...params, id: 'self' })
            .then((response) => {
                const hasNonBoolParam = Object.values(params).some((value) => typeof value !== 'boolean');

                // Only show toast for non-switch type fields
                if (hasNonBoolParam) {
                    createCustomToast('success', i18next.t('Toast.SettingsSavedSuccessfully'));
                }

                return response;
            })
            .catch(async (error: Response) => {
                if (error.status === 400) {
                    return rejectWithValue((await error.json()) as ValidationError);
                }

                throw error;
            });
    },
);

export const updateAccountAbout = createAsyncThunk<AccountEntity, UpdateTextParams>(
    'account/about/update',
    async (params, { rejectWithValue }) => {
        return api
            .updateUserAboutById({ ...params })
            .then((response) => {
                const shouldShowToast = updateTextParamsOrder.some((key) => {
                    const param = params[key];

                    return (param || param === '') && typeof param !== 'boolean';
                });

                if (shouldShowToast && window.location.pathname !== ROUTES.ONBOARDING) {
                    createCustomToast('success', `${i18next.t('Toast.SettingsSavedSuccessfully')}`);
                }

                return response;
            })
            .catch((err) => rejectWithValue(err));
    },
);

interface UpdateMatchSettingsParams {
    id: string;
    allowed_sex?: string;
    allowed_age?: string;
    allowed_countries?: string;
    allowed_languages?: string;
    native_speakers_only?: boolean;
    lfor?: string;
    enabled_filters?: boolean;
}

export const updateUserMatchSettings = createAsyncThunk<UserMatchSettingsEntity, UpdateMatchSettingsParams>(
    'account/match-settings/update',
    async (params, { rejectWithValue }) => {
        return api.updateUserMatchSettings({ ...params }).catch((err) => rejectWithValue(err));
    },
);

export const fetchAccountSettings = createAsyncThunk<SettingsEntity, void, { state: ReduxState }>(
    'account/settings/fetch',
    async (_, { getState, rejectWithValue }) => {
        const { account } = getState();

        if (account.data) {
            return api.getSettings({ id: account.data.id }).catch((err) => rejectWithValue(err));
        }

        return rejectWithValue('Account not found');
    },
);

// Fix uploading of profile photo in account reducer
export const makeProfilePhoto = createAsyncThunk<AlbumEntity | undefined, { albumId: string; photoId: string }>(
    'account/avatar/update',
    async ({ albumId, photoId }, { rejectWithValue }) => {
        return api.updateAlbum({ id: albumId, album_cover_id: photoId }).catch((err) => rejectWithValue(err));
    },
);

interface DeleteAccountParams {
    reason: string;
    afterAction: () => void;
}

export const deleteAccount = createAsyncThunk<void, DeleteAccountParams>(
    'account/delete',
    async (params: DeleteAccountParams, { dispatch, rejectWithValue }) => {
        return api
            .deleteAccount({ reason: params.reason })
            .then(() => {
                dispatch(clearState());
                params.afterAction();
            })
            .catch(async (err) => {
                createCustomToast('warning', `${i18next.t('Toast.AccountDeletionFailed')}`);

                return rejectWithValue(err);
            });
    },
);

export const reactivateAccount = createAsyncThunk<TokenEntity, undefined>(
    'account/reactivate',
    async (_, { dispatch, rejectWithValue }) => {
        return api
            .reactivateAccount()
            .then((token) => {
                dispatch(initLoggedInUser(token));

                return token;
            })
            .catch((err) => rejectWithValue(err));
    },
);
